import React, { useCallback, useEffect, useState } from "react";
import {
  fetchAnalyticsOverview,
  fetchBuyerAnalytics,
  fetchDeviceAnalytics,
  fetchOrderStats,
  fetchRevenueAnalytics,
  fetchRevenueStats,
  fetchUserStats,
} from "../../services/analyticsApi";
import { getCached } from "../../utils/adminCache";
import {
  DEFAULT_ANALYTICS_OVERVIEW,
  DEFAULT_BUYER_ANALYTICS,
  DEFAULT_DEVICE_ANALYTICS,
  DEFAULT_ORDER_STATS,
  DEFAULT_REVENUE_ANALYTICS,
  DEFAULT_REVENUE_STATS,
  DEFAULT_USER_STATS,
} from "../../utils/adminStatsDefaults";
import "../../styles/admin-common.css";

const defaultRange = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 30);
  return {
    startDate: start.toISOString().slice(0, 10),
    endDate: end.toISOString().slice(0, 10),
  };
};

const formatCurrency = (value) =>
  `₹${Number(value ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const formatStatus = (key) =>
  key.replace("Orders", "").replace(/([A-Z])/g, " $1").trim();

const Analytics = () => {
  const [range, setRange] = useState(defaultRange);
  const [overview, setOverview] = useState(DEFAULT_ANALYTICS_OVERVIEW);
  const [revenue, setRevenue] = useState(DEFAULT_REVENUE_ANALYTICS);
  const [revenueStats, setRevenueStats] = useState(DEFAULT_REVENUE_STATS);
  const [devices, setDevices] = useState(DEFAULT_DEVICE_ANALYTICS);
  const [buyers, setBuyers] = useState(DEFAULT_BUYER_ANALYTICS);
  const [orderStats, setOrderStats] = useState(DEFAULT_ORDER_STATS);
  const [userStats, setUserStats] = useState(DEFAULT_USER_STATS);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [warning, setWarning] = useState("");
  const [metricsError, setMetricsError] = useState(false);

  const cacheKey = `admin:analytics:bundle:${range.startDate}:${range.endDate}`;

  const load = useCallback(async (forceRefresh = false) => {
    const hasCache = !forceRefresh && getCached(cacheKey);
    if (!hasCache) setLoading(true);
    else setRefreshing(true);

    setWarning("");
    setMetricsError(false);
    let failures = 0;

    const wrap = async (promise, fallback, setter) => {
      try {
        const data = await promise;
        setter({ ...fallback, ...data });
        return data;
      } catch {
        failures += 1;
        setter(fallback);
        return fallback;
      }
    };

    const results = await Promise.all([
      wrap(fetchAnalyticsOverview(range.startDate, range.endDate, { forceRefresh }), DEFAULT_ANALYTICS_OVERVIEW, setOverview),
      wrap(fetchRevenueAnalytics(range.startDate, range.endDate, { forceRefresh }), DEFAULT_REVENUE_ANALYTICS, setRevenue),
      wrap(fetchRevenueStats({ forceRefresh }), DEFAULT_REVENUE_STATS, setRevenueStats),
      wrap(fetchDeviceAnalytics(range.startDate, range.endDate, { forceRefresh }), DEFAULT_DEVICE_ANALYTICS, setDevices),
      wrap(fetchBuyerAnalytics(range.startDate, range.endDate, { forceRefresh }), DEFAULT_BUYER_ANALYTICS, setBuyers),
      wrap(fetchOrderStats({ forceRefresh }), DEFAULT_ORDER_STATS, setOrderStats),
      wrap(fetchUserStats({ forceRefresh }), DEFAULT_USER_STATS, setUserStats),
    ]);

    try {
      sessionStorage.setItem(
        cacheKey,
        JSON.stringify({
          data: {
            overview: results[0],
            revenue: results[1],
            revenueStats: results[2],
            devices: results[3],
            buyers: results[4],
            orderStats: results[5],
            userStats: results[6],
          },
          expiresAt: Date.now() + 5 * 60 * 1000,
        })
      );
    } catch {
      // ignore
    }

    if (failures > 0) {
      setMetricsError(true);
      setWarning("Some analytics sections could not be loaded. Refresh to try again.");
    }

    setLoading(false);
    setRefreshing(false);
  }, [range.startDate, range.endDate, cacheKey]);

  useEffect(() => {
    const cached = getCached(cacheKey);
    if (cached) {
      setOverview({ ...DEFAULT_ANALYTICS_OVERVIEW, ...cached.overview });
      setRevenue({ ...DEFAULT_REVENUE_ANALYTICS, ...cached.revenue });
      setRevenueStats({ ...DEFAULT_REVENUE_STATS, ...cached.revenueStats });
      setDevices({ ...DEFAULT_DEVICE_ANALYTICS, ...cached.devices });
      setBuyers({ ...DEFAULT_BUYER_ANALYTICS, ...cached.buyers });
      setOrderStats({ ...DEFAULT_ORDER_STATS, ...cached.orderStats });
      setUserStats({ ...DEFAULT_USER_STATS, ...cached.userStats });
      setLoading(false);
    }
    load(false);
  }, [load, cacheKey]);

  const displayCount = (value) => (metricsError ? "—" : (value ?? 0));
  const displayCurrency = (value) => (metricsError ? "—" : formatCurrency(value));
  const displayDecimal = (value) => (metricsError ? "—" : Number(value ?? 0).toFixed(1));

  const periodCards = [
    { label: "Orders (period)", value: displayCount(overview.totalOrders) },
    { label: "Completed", value: displayCount(overview.completedOrders) },
    { label: "Cancelled", value: displayCount(overview.cancelledOrders) },
    { label: "New users", value: displayCount(overview.newUsers) },
    { label: "New vendors", value: displayCount(overview.newVendors) },
    { label: "Transaction volume", value: displayCurrency(overview.periodRevenue) },
    { label: "Completed value", value: displayCurrency(overview.completedOrderValue) },
  ];

  const revenueCards = [
    { label: "Vendor top-ups", value: displayCurrency(revenue.vendorTopUps) },
    { label: "Commission debits", value: displayCurrency(revenue.commissionDebits) },
    { label: "Holds placed", value: displayCurrency(revenue.holdsPlaced) },
    { label: "Holds released", value: displayCurrency(revenue.holdsReleased) },
    { label: "Successful volume", value: displayCurrency(revenue.successfulVolume) },
  ];

  const rollingRevenueCards = [
    { label: "Credits (7d)", value: displayCurrency(revenueStats.creditsLast7Days) },
    { label: "Debits (7d)", value: displayCurrency(revenueStats.debitsLast7Days) },
    { label: "Credits (30d)", value: displayCurrency(revenueStats.creditsLast30Days) },
    { label: "Debits (30d)", value: displayCurrency(revenueStats.debitsLast30Days) },
    { label: "Completed value (30d)", value: displayCurrency(revenueStats.completedValueLast30Days) },
    { label: "Wallet balance (all)", value: displayCurrency(revenueStats.totalWalletBalance) },
  ];

  const deviceCards = [
    { label: "Completed orders", value: displayCount(devices.completedOrders) },
    { label: "Avg price", value: displayCurrency(devices.averageCompletedPrice) },
    { label: "Total value", value: displayCurrency(devices.totalCompletedValue) },
  ];

  const buyerCards = [
    { label: "Active vendors", value: displayCount(buyers.activeBuyers) },
    { label: "Completed orders", value: displayCount(buyers.completedOrders) },
    { label: "Avg orders / vendor", value: displayDecimal(buyers.avgOrdersPerBuyer) },
  ];

  const userCards = [
    { label: "Total users", value: displayCount(userStats.totalUsers) },
    { label: "Active users", value: displayCount(userStats.activeUsers) },
    { label: "Customers", value: displayCount(userStats.userCount) },
    { label: "Vendors", value: displayCount(userStats.buyerCount) },
    { label: "New (30d)", value: displayCount(userStats.newUsersLast30Days) },
  ];

  const CardGrid = ({ title, cards }) => (
    <>
      <h3 className="section-heading">{title}</h3>
      <div className="analytics-grid">
        {cards.map((c) => (
          <div key={c.label} className="analytics-card">
            <span className="analytics-label">{c.label}</span>
            <span className="analytics-value">{c.value}</span>
          </div>
        ))}
      </div>
    </>
  );

  const orderStatusRows = Object.entries(orderStats).filter(([key]) => key.endsWith("Orders"));

  return (
    <div className="admin-page analytics-page">
      <div className="page-header-row">
        <div>
          <h2>Analytics</h2>
          <p className="subtitle">Period metrics, rolling revenue, device sales, and vendor performance.</p>
        </div>
        <button type="button" className="admin-btn secondary" onClick={() => load(true)} disabled={refreshing}>
          {refreshing ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      <div className="admin-filters">
        <label className="field-label">
          From
          <input type="date" value={range.startDate}
            onChange={(e) => setRange({ ...range, startDate: e.target.value })} />
        </label>
        <label className="field-label">
          To
          <input type="date" value={range.endDate}
            onChange={(e) => setRange({ ...range, endDate: e.target.value })} />
        </label>
      </div>

      {warning && <p className="warning-msg">{warning}</p>}
      {loading && <div className="admin-loading">Loading analytics</div>}

      {!loading && (
        <>
          <CardGrid title="Period summary" cards={periodCards} />
          <CardGrid title="Rolling revenue" cards={rollingRevenueCards} />
          <CardGrid title="Revenue in selected period" cards={revenueCards} />
          <CardGrid title="Device sales" cards={deviceCards} />

          {(devices.topSellingDevices?.length ?? 0) > 0 ? (
            <div className="admin-card">
              <h4>Top selling models</h4>
              <table className="admin-table">
                <thead><tr><th>Model</th><th>Orders</th><th>Avg price</th></tr></thead>
                <tbody>
                  {devices.topSellingDevices.map((d) => (
                    <tr key={d.modelName}>
                      <td>{d.modelName}</td>
                      <td>{d.orderCount}</td>
                      <td>{formatCurrency(d.avgPrice)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="admin-card empty-state-card">
              <p>No completed device sales in this period.</p>
            </div>
          )}

          <CardGrid title="Vendor performance" cards={buyerCards} />

          {(buyers.topBuyers?.length ?? 0) > 0 ? (
            <div className="admin-card">
              <h4>Top vendors</h4>
              <table className="admin-table">
                <thead><tr><th>Vendor</th><th>Orders</th><th>Value</th></tr></thead>
                <tbody>
                  {buyers.topBuyers.map((b) => (
                    <tr key={b.buyerId ?? b.name}>
                      <td>{b.name}</td>
                      <td>{b.orderCount}</td>
                      <td>{formatCurrency(b.totalValue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="admin-card empty-state-card">
              <p>No vendor activity in this period.</p>
            </div>
          )}

          <CardGrid title="Users" cards={userCards} />

          <div className="admin-card">
            <h3 className="section-heading">Orders by status (all time)</h3>
            {orderStatusRows.length > 0 ? (
              <table className="admin-table">
                <thead><tr><th>Status</th><th>Count</th></tr></thead>
                <tbody>
                  {orderStatusRows.map(([key, value]) => (
                    <tr key={key}>
                      <td>{formatStatus(key)}</td>
                      <td>{metricsError ? "—" : (value ?? 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="empty-msg">No orders recorded yet.</p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Analytics;
